import { DOWN_ARROW, UP_ARROW } from '@angular/cdk/keycodes';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    inject,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatExpansionPanel } from '@angular/material/expansion';
import { MatLegacyAutocomplete as MatAutocomplete } from '@angular/material/legacy-autocomplete';
import { Router } from '@angular/router';
import { IAppRequest } from '@app/app.interface';
import { AppService } from '@app/app.service';
import { CustomValidators } from '@app/app.validator';
import { AppHelper } from '@app/core/app.helper';
import { MapDialogComponent } from '@app/dialogs-helper/map-dialog/map-dialog.component';
import { TButtonColor } from '@components/button/constants/button.types';
import { AppDialogService } from '@components/dialog/services/app-dialog.service';
import { getStringPresentation } from '@helpers/address-helper';
import { fiasAddressLevels, IAddress, IAddressVisibility } from '@interfaces/address.interface';
import { NotifierService } from '@services/notifier.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import {
    catchError,
    debounceTime,
    filter,
    finalize,
    map,
    skip,
    startWith,
    switchMap,
    take,
    takeUntil,
    tap,
} from 'rxjs/operators';
import { FieldConfig } from '../../field.interface';
import { EDivisions } from '../enums/EDivisions';
import { MarkerStates } from '../enums/MarkerStates';
import { IAddressLoading, IBindCoordinates } from '../interface/address.interface';

@Component({
    selector: 'app-input',
    templateUrl: './address.component.html',
    styleUrls: ['../styles/address.component.css', '../../input/input.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddressComponent implements OnInit, OnChanges, OnDestroy {
    private dialogConfirm = inject(AppDialogService);
    private readonly router = inject(Router);
    private readonly fb = inject(FormBuilder);
    public readonly appService = inject(AppService);
    public readonly spinner = inject(NgxSpinnerService);
    public readonly notifier = inject(NotifierService);
    public readonly dialog = inject(MatDialog);
    public readonly cdr = inject(ChangeDetectorRef);

    private _destroy: Subject<void> = new Subject();

    field: FieldConfig;
    group: FormGroup;
    data: any = {
        id: null,
        findAddressField: null,
        addressDefault: null,
        value: null,
        disabled: false,
        regionNotValid: false,
        type: 'medium',
        hideMarker: false,
    };
    isLoadingSimple = false;

    /** Флаг состояния окна */
    private isOpen$ = new BehaviorSubject<boolean>(false);

    /** Задизаблен ли маркер? */
    public isMarkerDisabled: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    /** Цвет маркера */
    public markerColor: BehaviorSubject<TButtonColor> = new BehaviorSubject<TButtonColor>('flat');

    @Output() isOpenPanel = this.isOpen$.asObservable().pipe(skip(1));

    /**
     * Опции для автокомплита
     */
    filteredOptionsSimple = null;

    /** Флаг показа муниципальных контролов */
    isShowMunicipal$: BehaviorSubject<boolean> = new BehaviorSubject(false);

    /**
     * Объект формы
     */
    form: FormGroup = new FormGroup<any>({});
    mainForm: FormGroup = new FormGroup<any>({});

    /**
     * Опции для автокомплита
     */
    filteredAddressOptions = {
        subject: null,
        administrative_region: null,
        municipal_region: null,
        urban_settlement: null,
        city: null,
        locality: null,
        street: null,
        el_road_network: null,
        land_plot: null,
        house: null,
        apartment: null,
        room: null,
        lvl_autonomous_okrug: null,
        lvl_inner_city_territory: null,
        lvl_additional_territories: null,
        lvl_objects_add_territories: null,
        parking_space: null,
    };

    /**
     * Уровни адресов
     */
    addressLevels = {
        subject: [1],
        administrative_region: [2],
        municipal_region: [3],
        urban_settlement: [4],
        city: [5],
        locality: [6],
        street: [7],
        el_road_network: [8],
        land_plot: [9],
        house: [10],
        apartment: [11],
        room: [12],
        lvl_autonomous_okrug: [13],
        lvl_inner_city_territory: [14],
        lvl_additional_territories: [15],
        lvl_objects_add_territories: [16],
        parking_space: [17],
    };

    /**
     * Уровни адреса по умолчанию
     */
    defaultAddressLayers = {
        is_region: 'region_name',
        is_district: 'district_name',
        is_city: 'city_name',
        is_street: 'street_name',
        is_house: 'house_name',
        is_room: 'room_name',
    };

    /**
     * Ссылка на объект формы
     */
    @ViewChild('formRef', { static: false }) formRef: ElementRef;
    @ViewChild('autoAddress', { static: false }) autoAddress: MatAutocomplete;
    @ViewChild('expansionPanel', { static: false }) expansionPanel: MatExpansionPanel;

    isLoading: IAddressLoading = {
        subject: false,
        administrative_region: false,
        municipal_region: false,
        urban_settlement: false,
        city: false,
        locality: false,
        street: false,
        el_road_network: false,
        land_plot: false,
        house: false,
        apartment: false,
        room: false,
        parking_space: false,
        lvl_autonomous_okrug: false,
        lvl_inner_city_territory: false,
        lvl_additional_territories: false,
        lvl_objects_add_territories: false,
    };
    /**
     * Флаг очистки дочерних элементов
     */
    isClearField = true;
    /**
     * Проставление данных из основной формы
     */
    isSetIsMain = false;
    /**
     * Переменная для отслеживания событий
     */
    fn: any;

    /**
     * Значение для проверки на пустоту
     */
    valueSelect: any;

    readonly EDivisions = EDivisions;

    /**
     * Настройки видимости частей адреса
     */
    get addressVisibility(): IAddressVisibility | null {
        const displaySettings = this.data.addressDefault?.display_settings ?? null;
        if (displaySettings === null) {
            return null;
        }

        // на случай если в display settings сразу объект IAddressVisibility
        if (typeof displaySettings !== 'string') {
            return displaySettings;
        }

        // на случай если в display settings строка json
        try {
            return JSON.parse(displaySettings);
        } catch {
            return null;
        }
    }

    /**
     * Функция отображения данных главного автокомплита
     */
    getAddressPresentation = (item: IAddress) => getStringPresentation(item, this.addressVisibility);

    /**
     * Возвращает название класса
     * @param option
     * @private
     */
    allowSelection(option: string): { [className: string]: boolean } {
        this.valueSelect = option;
        return {
            'no-data': option === this.appService.language_dictionary.address_not_found_in_fias,
        };
    }

    /**
     * Событие окончания инициализации компонента
     */
    ngOnInit() {
        this.mainForm = this.fb.group({
            [this.field.name]: [
                this.group.get(this.field.name).value,
                this.field.required ? [Validators.required] : [],
            ],
        });

        // Сразу обновим состояние маркера при первой инициализации
        this.updateMarker(true);

        // Если пустой адрес блокируем выбор с помощью стрелок
        this.fn = (evt: KeyboardEvent) => {
            if (evt.keyCode === DOWN_ARROW || evt.keyCode === UP_ARROW) {
                if (
                    this.valueSelect &&
                    this.valueSelect === this.appService.language_dictionary.address_not_found_in_fias
                ) {
                    evt.stopPropagation();
                }
            }
        };

        this.filteredOptionsSimple = this.mainForm.get(this.field.name).valueChanges.pipe(
            startWith(''),
            debounceTime(1000),
            tap((value) => (this.isLoadingSimple = !!value)),
            switchMap((value) => {
                if (value && typeof value === 'string') {
                    this.cdr.detectChanges();
                    return this.lookupMain({
                        substr: value,
                        count: 10,
                        parent: this.getLastIdStreet(value),
                    });
                } else {
                    this.isLoadingSimple = false;
                    return of(null);
                }
            }),
            takeUntil(this._destroy),
        );
        if (this.field.hideCoordinates) {
            this.mainForm
                .get(this.field.name)
                .valueChanges.pipe(takeUntil(this._destroy))
                .subscribe((value) => {
                    this.isSetIsMain = true;
                    if (typeof value == 'string') {
                        this.group.get(this.field.name).reset();
                        this.group.get(this.field.name).setValue({
                            additional_information: value,
                        });
                    } else {
                        this.group.get(this.field.name).setValue(value);
                    }
                    this.isSetIsMain = false;
                    this.group.get(this.field.name).markAsDirty();
                });
        }

        this.form = this.fb.group({
            search_type: [null, []],
            code_oksm: [null, []],
            country: [null, []],
            postalcode: [null, []],
            subject: [null, []],
            administrative_region: [null, []],
            municipal_region: [null, []],
            urban_settlement: [null, []],
            city: [null, []],
            locality: [null, []],
            street: [null, []],
            el_road_network: [null, []],
            land_plot: [null, []],
            house: [null, []],
            apartment: [null, []],
            room: [null, []],
            lvl_autonomous_okrug: [null, []],
            lvl_inner_city_territory: [null, []],
            lvl_additional_territories: [null, []],
            lvl_objects_add_territories: [null, []],
            parking_space: [null, []],
            additional_information: [null, []],
        });

        for (const option in this.filteredAddressOptions) {
            if (this.filteredAddressOptions.hasOwnProperty(option)) {
                this.filteredAddressOptions[option] = this.form.controls[option].valueChanges.pipe(
                    startWith(''),
                    debounceTime(1000),
                    tap((value) => (this.isLoading[option] = !!value)),
                    switchMap((value) => {
                        if (value && typeof value === 'string') {
                            this.cdr.detectChanges();
                            return this.lookup(option, {
                                substr: value,
                                parent: this.getLastId(option),
                                levels: this.addressLevels[option].join(','),
                                count: 10,
                            });
                        } else {
                            this.isLoading[option] = false;
                            return of(null);
                        }
                    }),
                    takeUntil(this._destroy),
                );
            }
        }
        this.setCustomValidate();
        this.group
            .get(this.field.name)
            .statusChanges.pipe(takeUntil(this._destroy))
            .subscribe(() => {
                this.mainForm.get(this.field.name).setErrors(this.group.get(this.field.name).errors);
                this.mainForm.get(this.field.name).markAsTouched();
            });
        this.group
            .get(this.field.name)
            .valueChanges.pipe(takeUntil(this._destroy))
            .subscribe(() => {
                if (!this.isSetIsMain) {
                    this.mainForm.get(this.field.name).setValue(this.group.get(this.field.name).value);
                }
            });
        if (this.field.disabled || (this.data && this.data.disabled)) {
            setTimeout(() => {
                this.mainForm.get(this.field.name).disable({ onlySelf: true, emitEvent: false });
            });
        }
        this.setValueForm();

        this.mainForm.statusChanges
            .pipe(
                filter((status) => status === 'INVALID'),
                debounceTime(0),
                takeUntil(this._destroy),
            )
            .subscribe(() => {
                this.mainForm.markAsTouched();
                this.cdr.detectChanges();
            });

        // Обновим маркер при изменении значений поля
        this.mainForm.valueChanges.pipe(takeUntil(this._destroy)).subscribe(() => this.updateMarker());

        document.addEventListener('keydown', this.fn, true);
    }

    /**
     * Изменение параметров компонента
     */
    ngOnChanges(changes: SimpleChanges): void {
        if (changes.field) {
            if (changes.field.currentValue.required) {
                this.mainForm.get(this.field.name)?.addValidators(Validators.required);
            } else {
                this.mainForm.get(this.field.name)?.removeValidators(Validators.required);
            }
        }
        this.cdr.markForCheck();
    }

    /**
     * Изменение данных компонента
     */
    changeData() {
        if (this.field.disabled || (this.data && this.data.disabled)) {
            this.mainForm.get(this.field.name).disable({ onlySelf: true, emitEvent: false });
        } else {
            this.mainForm.get(this.field.name).enable({ onlySelf: true, emitEvent: false });
        }
    }

    /**
     * Получение последнего ид до дома
     */
    getLastIdStreet(valueString) {
        const value = this.group.get(this.field.name).value;
        let id = null;
        if (value && value?.fullname && value.parents) {
            value.parents.forEach((parent) => {
                if (valueString.includes(parent?.fullname)) {
                    id = parent.id;
                }
            });
            if (valueString.includes(value?.fullname)) {
                id = value.id;
            }
        }
        return id;
    }

    /**
     * Получение последнего заполненного ИД
     * @param type тип
     */
    getLastId(type: string) {
        let last = null;

        for (const option in this.filteredAddressOptions) {
            if (this.filteredAddressOptions.hasOwnProperty(option)) {
                if (option === type) {
                    break;
                }
                if (this.form.get(option).value && this.form.get(option).value.id) {
                    last = this.form.get(option).value.id;
                }
            }
        }
        return last;
    }

    /**
     * Очистка поля
     * @param field поле проверки
     * @param clear поле очистки
     */
    clearField(field: string, clear: string) {
        if (this.isClearField && !(this.form.value[field] && this.form.value[field].id)) {
            this.form.controls[clear].setValue(null);
        }
    }

    /**
     * Очистка полей по уровню
     * @param lvl уровень после которого необходимо очистить поля
     * @param customLvlToClear Касомные уровни для очистки
     */
    clearFieldsByLvl(lvl: number = -1, customLvlToClear: number[] = []) {
        const clearIndexLvl = [1, 2];

        if (lvl !== -1) {
            Object.assign([], fiasAddressLevels).forEach((value, currentLvl) => {
                if (Number(currentLvl) > Number(lvl)) {
                    // Проставляем пустое значение
                    if (clearIndexLvl.includes(lvl)) {
                        this.form.get('postalcode').setValue(null);
                    }
                    this.form.controls[value].setValue(null);
                }
            });
        }
        if (customLvlToClear.length > 0) {
            customLvlToClear.forEach((key) => {
                this.form.controls[fiasAddressLevels[key]].setValue(null);
            });
        }
    }

    /**
     * Установка значений дочернего поля
     * @param field поле
     */
    setValueChildren(field: string) {
        if (this.form.value[field] && this.form.value[field].parents && this.form.value[field].parents.length > 0) {
            this.isClearField = false;
            this.form.value[field].parents.forEach((parent) => {
                this.form.controls[fiasAddressLevels[parent.level]].setValue(parent);
            });
            this.isClearField = true;
        }
    }

    /**
     * Проставление данных индекса
     * @param field поле
     */
    setPostalCode(field: string) {
        const value = this.form.get(field).value;
        this.form.get('postalcode').setValue(value && value.postalcode ? value.postalcode : null);
    }

    /**
     * Установка валидации для полей зависящих от даты
     */
    setCustomValidate() {
        const controlSubject = this.form.get('subject');
        if (!this.data || (this.data && !this.data.regionNotValid)) {
            controlSubject.setValidators([CustomValidators.autocomplete]);
        } else {
            controlSubject.setValidators([]);
        }
        controlSubject.updateValueAndValidity();
    }

    /**
     * Функция отображения данных автокомплита
     * @param address данные по адресу
     */
    displayFn(address?: any): string | undefined {
        const result = [];
        let name = [];
        if (address && address.name) {
            name = address.name.split(', ');
            if (name.length > 1) {
                name.forEach((value, key) => {
                    result.push(value);
                    if (key === 0) {
                        if (address && address.shortname) {
                            result.push(address.shortname + '.,');
                        }
                    }
                });
            } else {
                result.push(address.name);
            }
        }
        if (address && address.shortname && name.length < 2) {
            result.push(address.shortname + '.');
        }
        return result.join(' ');
    }

    /**
     * Отправка запроса главного поля
     * @param data данные запроса
     */
    lookupMain(data: any): Observable<any> {
        let additionalInformation = null;
        if (this.group.get(this.field.name).value) {
            additionalInformation = this.group.get(this.field.name).value.additional_information;
        }
        const obj = {
            type: this.searchTypeByForm,
            method: 'find_addrob',
            data,
        };
        return this.appService.getFiasAddressByApi(obj).pipe(
            map((results) => {
                if (results.is_error) {
                    return [{ fullname: this.appService.language_dictionary.fias_service_is_unavailable }];
                }
                if (!results.data?.length) {
                    return [{ fullname: this.appService.language_dictionary.address_not_found_in_fias }];
                }
                results.data.forEach((v: IAddress) => {
                    v.additional_information = additionalInformation;
                    v.search_type = this.searchTypeByForm;
                });
                return results.data;
            }),
            catchError((_: unknown) => {
                return [{ fullname: this.appService.language_dictionary.fias_service_is_unavailable }];
            }),
            finalize(() => (this.isLoadingSimple = false)),
        );
    }

    /**
     * Отправка запроса формы
     * @param type тип запроса
     * @param data данные запроса
     */
    lookup(type: string, data: any): Observable<any> {
        const obj = {
            type: this.searchTypeByForm,
            method: 'find_by_parent',
            data,
        };
        return this.appService.getFiasAddressByApi(obj).pipe(
            map((results) => {
                if (results.is_error) {
                    return [{ fullname: this.appService.language_dictionary.fias_service_is_unavailable }];
                }
                if (!results.data?.length) {
                    return [{ fullname: this.appService.language_dictionary.address_not_found_in_fias }];
                }
                return results.data;
            }),
            catchError((_: unknown) => {
                return [{ fullname: this.appService.language_dictionary.fias_service_is_unavailable }];
            }),
            finalize(() => (this.isLoading[type] = false)),
        );
    }

    /**
     * Сохранение данных
     */
    save() {
        if (!this.form.valid) {
            AppHelper.validateAllFormFields(this.form);
            AppHelper.focusFirstInvalidField(this.formRef);
            return;
        }

        const data = Object.assign({}, this.form.value);
        let lastSelected: any = null;
        const addressHierarchy: any[] = [];
        let fullAddressName = '';

        // Проходим по всем полям адреса (улица, город, регион и т.п.)
        for (const key in this.filteredAddressOptions) {
            if (!this.filteredAddressOptions.hasOwnProperty(key)) {
                continue;
            }

            const item = data[key];
            if (item && item.id) {
                // Сохраняем текущий уровень адреса
                addressHierarchy.push(item);
                // Запоминаем последний элемент адреса
                lastSelected = Object.assign({}, data[key]);
                // Обновляем полное название
                fullAddressName = item.fullname;
                // Очищаем ненужные данные, чтобы не дублировались
                delete data[key].parents;
            }
        }

        // Если выбраны уровни адреса, сохраняем иерархию в последнем элементе
        if (lastSelected && addressHierarchy.length) {
            lastSelected.parents = addressHierarchy;
        }

        // Формируем итоговый объект результата
        const result: any = lastSelected ? lastSelected : {};

        // Добавляем прочие значения формы
        result.postalcode = this.form.get('postalcode')?.value;
        result.additional_information = data.additional_information;
        result.fullname = fullAddressName;
        result.search_type = data.search_type;

        // Записываем результат в форму
        this.mainForm.get(this.field.name).setValue(result);
        this.group.get(this.field.name).setValue(result);
        this.expansionPanel.close();
        this.isOpen$.next(false);
        this.setCoordinates();
        this.mainForm.get(this.field.name).enable({ onlySelf: true, emitEvent: false });
    }

    /**
     * Закрытие формы
     */
    close() {
        this.setValueForm();
        this.isOpen$.next(false);
        this.expansionPanel.close();
    }

    /**
     * Проставление данных на форму
     */
    setValueForm() {
        const value = Object.assign({}, this.group.get(this.field.name).value);
        this.changeAutoComplete(value);
        if (value && value.level) {
            this.isClearField = false;
            // Строка закомментирована т.к. вызывает ошибку:
            // Восстановление значения дома в поле улицы
            // This.form.get(this.levelsAddress[value.level]).setValue(value);
            this.isClearField = true;
        }
    }

    /**
     * Выбор записи в автокомплите
     * @param value значение
     * @param coordinates координаты
     */
    changeAutoComplete(value: any, coordinates: number[] = null) {
        this.isClearField = false;
        this.changedDivision(value.search_type ?? this.initSearchType);
        this.form.patchValue({
            additional_information: value && value.additional_information ? value.additional_information : null,
            postalcode: value && value.postalcode ? value.postalcode : null,
        });

        if (value && value.parents && value.parents.length) {
            value.parents.forEach((parent) => {
                // Если в массиве this.addressLevels.region
                // Есть level текущего parent, т.е. части адреса
                // Значит это регион (и то же для остальных полей)
                if (this.addressLevels.subject.indexOf(Number(parent.level)) !== -1) {
                    this.form.get('subject').setValue(parent);
                }
                if (this.addressLevels.administrative_region.indexOf(Number(parent.level)) !== -1) {
                    this.form.get('administrative_region').setValue(parent);
                }
                if (this.addressLevels.municipal_region.indexOf(Number(parent.level)) !== -1) {
                    this.form.get('municipal_region').setValue(parent);
                }
                if (this.addressLevels.urban_settlement.indexOf(Number(parent.level)) !== -1) {
                    this.form.get('urban_settlement').setValue(parent);
                }
                if (this.addressLevels.city.indexOf(Number(parent.level)) !== -1) {
                    this.form.get('city').setValue(parent);
                }
                if (this.addressLevels.locality.indexOf(Number(parent.level)) !== -1) {
                    this.form.get('locality').setValue(parent);
                }
                if (this.addressLevels.street.indexOf(Number(parent.level)) !== -1) {
                    this.form.get('street').setValue(parent);
                }
                if (this.addressLevels.el_road_network.indexOf(Number(parent.level)) !== -1) {
                    this.form.get('el_road_network').setValue(parent);
                }
                if (this.addressLevels.land_plot.indexOf(Number(parent.level)) !== -1) {
                    this.form.get('land_plot').setValue(parent);
                }
                if (this.addressLevels.house.indexOf(Number(parent.level)) !== -1) {
                    this.form.get('house').setValue(parent);
                }
                if (this.addressLevels.apartment.indexOf(Number(parent.level)) !== -1) {
                    this.form.get('apartment').setValue(parent);
                }
                if (this.addressLevels.room.indexOf(Number(parent.level)) !== -1) {
                    this.form.get('room').setValue(parent);
                }
                if (this.addressLevels.lvl_autonomous_okrug.indexOf(Number(parent.level)) !== -1) {
                    this.form.get('lvl_autonomous_okrug').setValue(parent);
                }
                if (this.addressLevels.lvl_inner_city_territory.indexOf(Number(parent.level)) !== -1) {
                    this.form.get('lvl_inner_city_territory').setValue(parent);
                }
                if (this.addressLevels.lvl_additional_territories.indexOf(Number(parent.level)) !== -1) {
                    this.form.get('lvl_additional_territories').setValue(parent);
                }
                if (this.addressLevels.lvl_objects_add_territories.indexOf(Number(parent.level)) !== -1) {
                    this.form.get('lvl_objects_add_territories').setValue(parent);
                }
                if (this.addressLevels.parking_space.indexOf(Number(parent.level)) !== -1) {
                    this.form.get('parking_space').setValue(parent);
                }
            });
        }

        this.isClearField = true;
        this.setCustomValidate();
        if (!coordinates) {
            this.setCoordinates();
        } else {
            const valueMain = this.mainForm.get(this.field.name).value;
            if (valueMain && typeof valueMain === 'object') {
                valueMain.coordinates = coordinates;
                this.mainForm.get(this.field.name).setValue(valueMain);
                this.group.get(this.field.name).setValue(valueMain);
            }
        }
    }

    /**
     * Редактирование координат
     */
    editCoordinates(ignoreExistGeometry: boolean | null = null) {
        if (typeof this.mainForm.get(this.field.name).value !== 'object') {
            return;
        }
        const data: IBindCoordinates = {
            x: this.mainForm.get(this.field.name).value.coordinates[1],
            y: this.mainForm.get(this.field.name).value.coordinates[0],
            epsg: 'EPSG:4326',
            is_object: ignoreExistGeometry === null ? true : ignoreExistGeometry,
            source: 'address',
        };
        this.spinner.show();
        this.appService
            .bindCoordinates(data, this.data?.id)
            .toPromise()
            .then((result) => {
                this.spinner.hide();
                if (result.is_error) {
                    if (result.has_geo_from_map) {
                        this.showRewriteGeometryConfirmation();
                        return;
                    }

                    this.notifier.notify('error', result.message);
                } else {
                    this.setMarkerState(MarkerStates.Linked);
                    this.notifier.notify('success', this.appService.language_dictionary.binding_was_successful);
                }
            })
            .catch(() => {
                this.spinner.hide();
                this.notifier.notify('error', this.appService.language_dictionary.system_no_connection_with_system);
            });
    }

    /**
     * Открыть диалог подтверждения перезаписи существующей геометрии
     */
    showRewriteGeometryConfirmation() {
        this.dialogConfirm
            .confirm(
                this.appService.language_dictionary.system_confirm_action,
                this.appService.language_dictionary.rewrite_map_geometry_confirmation,
            )
            .pipe(takeUntil(this._destroy))
            .subscribe((res) => {
                if (res) {
                    this.editCoordinates(false);
                }
            });
    }

    /**
     * Проставление координат адресу
     */
    setCoordinates() {
        const value = this.mainForm.get(this.field.name).value;
        if (value == null || typeof value !== 'object') {
            return;
        }
        if (Object.keys(value).length !== 0 && !value.lonlat && !value.coordinates && value.fullname) {
            const obj = {
                type: this.searchTypeByForm,
                method: 'find_addrob',
                data: {
                    substr: value?.fullname,
                    count: 10,
                },
            };

            this.appService
                .getFiasAddressByApi(obj)
                .toPromise()
                .then((result) => {
                    if (result.is_error) {
                        this.notifier.warning(this.appService.language_dictionary.fias_service_is_unavailable);
                        this.setCoordinatesByYandex(value);
                        return;
                    }
                    const firstResult = result.data?.length > 0 ? result.data[0] : null;
                    const coordinates = firstResult?.lonlat;
                    if (coordinates) {
                        value.coordinates = [coordinates[1], coordinates[0]];
                        value.search_type = this.searchTypeByForm;
                        this.mainForm.get(this.field.name).setValue(value);
                        this.group.get(this.field.name).setValue(value);
                    } else {
                        this.setCoordinatesByYandex(value);
                    }
                })
                .catch(() => {
                    this.setCoordinatesByYandex(value);
                    this.notifier.warning(this.appService.language_dictionary.fias_service_is_unavailable);
                });
        } else if (Object.keys(value).length !== 0 && value.lonlat && !value.coordinates) {
            const coordinates = value.lonlat;
            value.coordinates = [coordinates[1], coordinates[0]];
            value.search_type = this.searchTypeByForm;
            this.mainForm.get(this.field.name).setValue(value);
            this.group.get(this.field.name).setValue(value);
        }
    }

    /**
     * Проставление координат через яндекс декодер.
     * @param value
     */
    setCoordinatesByYandex(value) {
        if (!value || typeof value !== 'object' || !value.fullname) {
            return;
        }

        this.appService
            .getYandexGeocode({
                format: 'json',
                apikey: this.appService.settings.geocode_api_key,
                geocode: value.fullname,
            })
            .toPromise()
            .then((res) => {
                if (
                    res &&
                    res.response &&
                    res.response.GeoObjectCollection &&
                    res.response.GeoObjectCollection.featureMember &&
                    res.response.GeoObjectCollection.featureMember.length &&
                    res.response.GeoObjectCollection.featureMember[0] &&
                    res.response.GeoObjectCollection.featureMember[0].GeoObject &&
                    res.response.GeoObjectCollection.featureMember[0].GeoObject.Point &&
                    res.response.GeoObjectCollection.featureMember[0].GeoObject.Point.pos
                ) {
                    const coordinates =
                        res.response.GeoObjectCollection.featureMember[0].GeoObject.Point.pos.split(' ');
                    value.coordinates = [coordinates[1], coordinates[0]];
                    this.mainForm.get(this.field.name).setValue(value);
                    this.group.get(this.field.name).setValue(value);
                }
            })
            .catch((e) => {
                console.error(e);
            });
    }

    /**
     * Редактирование координат
     */
    editAddressByCoordinates() {
        const value = this.group.get(this.field.name).value;
        const dialogRef = this.dialog.open(MapDialogComponent, {
            disableClose: true,
            autoFocus: true,
            width: '600px',
            data: {
                title: this.appService.language_dictionary.system_geocode_title_address_dialog,
                data: {
                    coordinates: value && value.coordinates ? [value.coordinates[0], value.coordinates[1]] : null,
                },
            },
        });
        dialogRef
            .keydownEvents()
            .pipe(takeUntil(this._destroy))
            .subscribe((result) => {
                if (result.code === 'Escape') {
                    dialogRef.close({ save: false });
                }
            });
        dialogRef
            .afterClosed()
            .pipe(takeUntil(this._destroy))
            .subscribe((result) => {
                if (result.save && result.data) {
                    let coordinates = null;
                    if (result.data?.coordinates) {
                        coordinates = result.data?.coordinates;
                    }
                    this.findAndSetAddress(result.data?.address, coordinates);
                }
            });
    }

    /**
     * Поиск и проставление адреса
     * @param address строка адреса
     * @param coordinates координаты
     * @param parent UUID родителя
     */
    findAndSetAddress(address: string, coordinates: number[] = null, parent: string = null) {
        const data: any = {
            substr: address,
            count: 10,
        };
        if (parent) {
            data.parent = parent;
        }

        const obj = {
            type: this.searchTypeByForm,
            method: 'find_addrob',
            data,
        };

        this.appService
            .getFiasAddressByApi(obj)
            .pipe(takeUntil(this._destroy))
            .subscribe({
                next: (results) => {
                    if (results.is_error) {
                        this.notifier.warning(this.appService.language_dictionary.fias_service_is_unavailable);
                        return;
                    }
                    let resultAddress: any = { additional_information: address };
                    if (results.data.length) {
                        resultAddress = results.data[0];
                        this.setAddressFromCoords(resultAddress, coordinates);
                    } else {
                        const addressArray = address.split(',');
                        const parseAddressArray = [];
                        let parseAddress = [];
                        addressArray.forEach((addressString) => {
                            parseAddressArray.push(addressString);
                            parseAddress.push(parseAddressArray.join(','));
                        });
                        parseAddress = parseAddress.slice(0, -1);
                        parseAddress = parseAddress.reverse();
                        this.recursiveFindFiasAddress(parseAddress, address, coordinates);
                    }
                },
                error: () => this.notifier.warning(this.appService.language_dictionary.fias_service_is_unavailable),
            });
    }

    /**
     * Рекурсивный поиск адреса в фиас
     * @param parseAddress массив адресов
     * @param fullAddress полная строка адреса
     * @param coordinates координаты
     */
    recursiveFindFiasAddress(parseAddress, fullAddress, coordinates) {
        if (parseAddress && parseAddress.length) {
            const obj = {
                type: this.searchTypeByForm,
                method: 'find_addrob',
                data: {
                    substr: parseAddress[0],
                    count: 10,
                },
            };
            this.appService
                .getFiasAddressByApi(obj)
                .pipe(take(1), takeUntil(this._destroy))
                .subscribe({
                    next: (results) => {
                        if (results.is_error) {
                            this.notifier.warning(this.appService.language_dictionary.fias_service_is_unavailable);
                            return;
                        }
                        if (results.data.length) {
                            const resultAddress = results.data[0] as IAddress;
                            resultAddress.additional_information = fullAddress.replace(parseAddress[0] + ', ', '');
                            this.setAddressFromCoords(resultAddress, coordinates);
                        } else {
                            parseAddress = parseAddress.slice(1);
                            this.recursiveFindFiasAddress(parseAddress, fullAddress, coordinates);
                        }
                    },
                    error: () => this.notifier.warning(this.appService.language_dictionary.fias_service_is_unavailable),
                });
        }
    }

    /**
     * Проставление адреса после поиска координат
     * @param resultAddress аддресс
     * @param coordinates координаты
     */
    setAddressFromCoords(resultAddress: any, coordinates: number[] = null) {
        this.mainForm.get(this.field.name).setValue(resultAddress);
        this.changeAutoComplete(resultAddress, coordinates);
        if (resultAddress && resultAddress.level) {
            this.isClearField = false;
            this.form.get(fiasAddressLevels[resultAddress.level]).setValue(resultAddress);
            this.isClearField = true;
        }
    }

    /**
     * Поиск адреса по слоям карты
     */
    editAddressByFindAddress() {
        this.spinner.show();
        this.appService
            .findAddressByMethod({
                factor_data_id: this.data.id,
                method_id: this.data.findAddressField,
            })
            .toPromise()
            .then((result: IAppRequest) => {
                this.spinner.hide();
                if (result && !result.is_error) {
                    if (result.data?.address) {
                        const parentName = [];
                        if (this.data.addressDefault) {
                            Object.keys(this.defaultAddressLayers).forEach((key) => {
                                if (
                                    this.data.addressDefault[key] &&
                                    this.data.addressDefault[key] === '1' &&
                                    this.data.addressDefault[this.defaultAddressLayers[key]]
                                ) {
                                    parentName.push(this.data.addressDefault[this.defaultAddressLayers[key]]);
                                }
                            });
                        }
                        this.findAndSetAddress(parentName.join(', ') + result.data.address);
                    } else {
                        this.notifier.notify(
                            'error',
                            this.appService.language_dictionary.system_geocode_find_address_error,
                        );
                    }
                } else {
                    this.notifier.notify('error', result.message);
                }
            })
            .catch(() => {
                this.spinner.hide();
                this.notifier.notify('error', this.appService.language_dictionary.system_no_connection_with_system);
            });
    }

    /**
     * Очистка поля
     */
    clearInput() {
        this.mainForm.get(this.field.name).setValue([]);
        this.group.get(this.field.name).setValue([]);
        const division = this.searchTypeByForm;
        this.form.reset();
        this.form.get('search_type').setValue(division);
        this.mainForm.get(this.field.name).markAsDirty();
    }

    /**
     * Выборочное очищение формы
     */
    customClearForm() {
        const division = this.searchTypeByForm;
        this.form.reset();
        this.form.get('search_type').setValue(division, { emitEvent: false });
    }

    /** Сброс формы */
    clearForm() {
        this.clearInput();
        const oldValueSearchType = this.form.get('search_type')?.value;
        this.form.reset({
            search_type: oldValueSearchType,
        });
    }

    /**
     * Выбор записи в автокомплите по полю
     * @param value значение
     */
    changeAutoCompleteField(value) {
        this.changeAutoComplete(value);
        this.group.get(this.field.name).setValue(value);
        this.group.get(this.field.name).markAsDirty();
    }

    /** Уничтожение компонента */
    ngOnDestroy() {
        this._destroy.next();
        this._destroy.complete();
    }

    /** Событие изменения деления адреса */
    public changedDivision(val: string) {
        if (val === EDivisions.municipal || val === EDivisions.administrative) {
            this.isShowMunicipal$.next(val === EDivisions.municipal);

            const valueMain = this.mainForm.get(this.field.name).value;
            if (valueMain && typeof valueMain === 'object') {
                this.mainForm.get(this.field.name).value.search_type = val;
            }
            if (this.group.get(this.field.name).value) {
                this.group.get(this.field.name).value.search_type = val;
            }
            this.form.get('search_type').setValue(val);
            this.customClearForm();
        }
    }

    /** Получение значения с формы вида поиска адреса */
    get searchTypeByForm() {
        return this.form.get('search_type').value;
    }

    /** Получение первоначального деления адреса(Административное/Муниципальное) */
    get initSearchType() {
        return this.data?.value?.search_type ?? this.data?.addressDefault?.search_type ?? EDivisions.administrative;
    }

    /** Переключение панели */
    public togglePanel() {
        const isOpen = this.isOpen$.value;
        if (isOpen) {
            this.isOpen$.next(false);
            this.expansionPanel.close();
        } else {
            this.isOpen$.next(true);
            this.expansionPanel.open();
        }
    }

    /**
     * Установка запрета изменения поля
     * @param disabled значение флага
     */
    setDisabled(disabled) {
        this.field.disabled = disabled;
        if (this.field.disabled) {
            this.group.get(this.field.name).disable({ onlySelf: true, emitEvent: false });
        } else {
            this.group.get(this.field.name).enable({ onlySelf: true, emitEvent: false });
        }
    }

    /**
     * Обновить маркер
     * @param isDefaultData Изменялись данные или нет
     */
    updateMarker(isDefaultData: boolean = false): void {
        const value = this.mainForm.get(this.field.name).value ?? '';
        if (typeof value === 'string' || (Array.isArray(value) && !value.length) || this.field.disabled) {
            this.setMarkerState(MarkerStates.Inactive);
            return;
        }

        if (!value.coordinates?.length) {
            this.setMarkerState(MarkerStates.Inactive);
            return;
        }

        // Если есть координаты и данные не были изменены
        if (isDefaultData && value.has_geo) {
            this.setMarkerState(MarkerStates.Linked);
            return;
        }

        this.setMarkerState(MarkerStates.Unlinked);
    }

    /**
     * Задать состояние маркера
     * @param state Состояние маркера
     */
    setMarkerState(state: MarkerStates): void {
        let color: TButtonColor = 'flat';
        if (state === MarkerStates.Linked) {
            color = 'soft';
        }

        this.markerColor.next(color);
        this.isMarkerDisabled.next(state === MarkerStates.Inactive);
    }

    /**
     * Перейти в конструктор реестров
     */
    openFactorSettings() {
        this.router.navigate(['factor', 'constructor', this.data.factorId, 'structure']);
    }
}
